import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { Button } from '../../components/Button';
import { API_URL } from '../../api/client';

interface NotificationPreferences {
  preference_id?: string;
  user_id?: string;
  sms_enabled: boolean;
  email_enabled: boolean;
  in_app_enabled: boolean;
  application_updates_enabled: boolean;
  payment_reminders_enabled: boolean;
  payment_confirmations_enabled: boolean;
  overdue_alerts_enabled: boolean;
  document_requests_enabled: boolean;
  account_updates_enabled: boolean;
  digest_mode: boolean;
  digest_time: string;
  created_at?: string;
  updated_at?: string;
}

export const NotificationSettings: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/notifications/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch notification preferences');
      }

      const data = await response.json();
      setPreferences(data.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load preferences');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (field: keyof NotificationPreferences) => {
    if (!preferences) return;

    setPreferences({
      ...preferences,
      [field]: !preferences[field],
    });
    setSuccess(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!preferences) return;

    setPreferences({
      ...preferences,
      digest_time: e.target.value,
    });
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!preferences) return;

    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error('Failed to update notification preferences');
      }

      const data = await response.json();
      setPreferences(data.data);
      setSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error && !preferences) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Notification Settings</h1>
        <p className="page-description">
          Manage how you receive notifications about your loans and applications
        </p>
      </div>

      {error && <ErrorMessage message={error} />}
      {success && (
        <div className="alert alert-success" style={{ marginBottom: '24px' }}>
          ✓ Notification preferences saved successfully!
        </div>
      )}

      {preferences && (
        <div className="settings-container">
          {/* Channel Preferences */}
          <section className="settings-section">
            <h2 className="section-title">Notification Channels</h2>
            <p className="section-description">
              Choose how you want to receive notifications
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">SMS Notifications</h3>
                  <p className="setting-description">
                    Receive text messages for important updates
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.sms_enabled}
                    onChange={() => handleToggle('sms_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Email Notifications</h3>
                  <p className="setting-description">
                    Receive email updates about your account
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.email_enabled}
                    onChange={() => handleToggle('email_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">In-App Notifications</h3>
                  <p className="setting-description">
                    Show notifications when you're using the app
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.in_app_enabled}
                    onChange={() => handleToggle('in_app_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </section>

          {/* Notification Types */}
          <section className="settings-section">
            <h2 className="section-title">Notification Types</h2>
            <p className="section-description">
              Control which types of notifications you receive
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Application Updates</h3>
                  <p className="setting-description">
                    Status changes on your loan applications
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.application_updates_enabled}
                    onChange={() => handleToggle('application_updates_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Payment Reminders</h3>
                  <p className="setting-description">
                    Reminders about upcoming loan payments
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.payment_reminders_enabled}
                    onChange={() => handleToggle('payment_reminders_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Payment Confirmations</h3>
                  <p className="setting-description">
                    Confirmations when payments are received
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.payment_confirmations_enabled}
                    onChange={() => handleToggle('payment_confirmations_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Overdue Alerts</h3>
                  <p className="setting-description">
                    Alerts when payments are overdue
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.overdue_alerts_enabled}
                    onChange={() => handleToggle('overdue_alerts_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Document Requests</h3>
                  <p className="setting-description">
                    When additional documents are required
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.document_requests_enabled}
                    onChange={() => handleToggle('document_requests_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Account Updates</h3>
                  <p className="setting-description">
                    Important account and security updates
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.account_updates_enabled}
                    onChange={() => handleToggle('account_updates_enabled')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </section>

          {/* Digest Mode */}
          <section className="settings-section">
            <h2 className="section-title">Digest Mode</h2>
            <p className="section-description">
              Batch non-urgent notifications into a daily summary
            </p>

            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <h3 className="setting-label">Enable Daily Digest</h3>
                  <p className="setting-description">
                    Receive a single daily summary instead of individual notifications
                  </p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={preferences.digest_mode}
                    onChange={() => handleToggle('digest_mode')}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {preferences.digest_mode && (
                <div className="setting-item">
                  <div className="setting-info">
                    <h3 className="setting-label">Digest Time</h3>
                    <p className="setting-description">
                      What time should we send your daily digest?
                    </p>
                  </div>
                  <input
                    type="time"
                    className="form-input"
                    value={preferences.digest_time}
                    onChange={handleTimeChange}
                    style={{ maxWidth: '150px' }}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Action Buttons */}
          <div className="settings-actions">
            <Button
              onClick={handleSave}
              variant="primary"
              loading={saving}
              disabled={saving}
            >
              Save Preferences
            </Button>
            <Button
              onClick={fetchPreferences}
              variant="secondary"
              disabled={saving}
            >
              Reset to Saved
            </Button>
          </div>
        </div>
      )}

      <style>{`
        .settings-container {
          max-width: 800px;
        }

        .settings-section {
          background: white;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .section-title {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 8px 0;
        }

        .section-description {
          font-size: 14px;
          color: #666;
          margin: 0 0 24px 0;
        }

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .setting-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 1px solid #f0f0f0;
        }

        .setting-item:last-child {
          border-bottom: none;
        }

        .setting-info {
          flex: 1;
          margin-right: 16px;
        }

        .setting-label {
          font-size: 15px;
          font-weight: 500;
          color: #1a1a1a;
          margin: 0 0 4px 0;
        }

        .setting-description {
          font-size: 13px;
          color: #666;
          margin: 0;
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 48px;
          height: 24px;
          flex-shrink: 0;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: var(--primary-color, #2563eb);
        }

        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(24px);
        }

        .settings-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #e5e5e5;
        }

        .alert {
          padding: 12px 16px;
          border-radius: 6px;
          font-size: 14px;
        }

        .alert-success {
          background-color: #d1fae5;
          color: #065f46;
          border: 1px solid #6ee7b7;
        }
      `}</style>
    </div>
  );
};
